export interface LocationOption {
    label: string;
    value: string;
    /** Market code of the event (NZ, AU, US). Null/empty is treated as NZ. */
    country?: string | null;
}

/** Event market the HDE location list adapts to. Only NZ and AU have distinct lists. */
export type HdeEventCountry = 'NZ' | 'AU';

/**
 * Resolve the market of the event the user is registering for, used to pick the
 * location list (NZ regions vs AU states/territories). Matches the selected event
 * by id, falling back to the first (nearest-upcoming) option. Anything that is not
 * explicitly AU — including null/empty and US — is treated as NZ.
 */
export function getHdeEventCountry(
    locationOptions: LocationOption[],
    eventId: string | null,
): HdeEventCountry {
    const selectedId = eventId?.split(',')[0]?.trim() || null;
    const match =
        (selectedId
            ? locationOptions.find((opt) => opt.value === selectedId)
            : undefined) ?? locationOptions[0];
    return match?.country?.trim().toUpperCase() === 'AU' ? 'AU' : 'NZ';
}

/**
 * Normalise ID to string for type-safe comparison.
 * GraphQL IDs can be string or number; form/Select values are always strings.
 */
export function toComparableId(id: string | number | null | undefined): string {
    if (id == null) return '';
    return String(id);
}

/**
 * Returns the event ID for the calendar link when multiple events are selected.
 * Uses the coming active event closest to now (first in locationOptions, which are
 * sorted by nearest upcoming date).
 */
export function getPrimaryEventId(
    eventId: string | null,
    locationOptions: LocationOption[],
): string | null {
    if (!eventId || !locationOptions.length) return null;
    const selectedIds = eventId
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);
    if (selectedIds.length === 0) return null;
    const primary = locationOptions.find((opt) =>
        selectedIds.includes(opt.value),
    );
    return primary?.value ?? selectedIds[0] ?? null;
}
